import { type JSX, type ReactNode, useCallback } from 'react';

import { createFileRoute, Outlet, useNavigate } from '@tanstack/react-router';

import { PageContent } from '@/components';
import {
  EssaysHeader,
  EssaysProvider,
  EssaysTabs,
  essaysSearchParamsSchema,
  type EssaysSearchParams,
} from '@/features/essays';
import { SchoolProvider, useSchoolContext } from '@/features/home-manager';
import { usePermissions } from '@/hooks/usePermissions';
import { requireAccess } from '@/permissions';

export const Route = createFileRoute('/redacoes')({
  staticData: { title: 'Redações' },
  beforeLoad: requireAccess('view:essays'),
  validateSearch: (search: Record<string, unknown>): EssaysSearchParams => essaysSearchParamsSchema.parse(search),
  component: EssaysLayout,
});

function EssaysLayout(): JSX.Element {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const { isAllowed } = usePermissions();
  const isStudent = isAllowed('view:student_essays');

  // `to: '.'` keeps the current tab. Without `to`, TanStack navigates to this layout's path (/redacoes), whose
  // index redirect remounts the tab and resets its state (e.g. the pagination).
  const handleNavigate = useCallback(
    ({ search: nextSearch, replace }: { search: EssaysSearchParams; replace?: boolean }) => {
      void navigate({ to: '.', search: nextSearch, replace });
    },
    [navigate]
  );

  const content = (
    <>
      <EssaysHeader />
      <PageContent>
        <EssaysTabs />
        <Outlet />
      </PageContent>
    </>
  );

  if (isStudent) {
    return (
      <EssaysProvider
        search={search}
        navigate={handleNavigate}
        isStudent>
        {content}
      </EssaysProvider>
    );
  }

  return (
    <SchoolProvider
      schoolId={search.escola !== undefined ? String(search.escola) : null}
      onSchoolIdChange={(schoolId) =>
        handleNavigate({ search: { ...search, escola: Number(schoolId), page: undefined }, replace: true })
      }>
      <ManagerEssaysProvider
        search={search}
        navigate={handleNavigate}>
        {content}
      </ManagerEssaysProvider>
    </SchoolProvider>
  );
}

type ManagerEssaysProviderProps = {
  children: ReactNode;
  search: EssaysSearchParams;
  navigate: (options: { search: EssaysSearchParams; replace?: boolean }) => void;
};

// Reads the selected school from SchoolProvider, which only exists for managers.
function ManagerEssaysProvider({ children, search, navigate }: ManagerEssaysProviderProps): JSX.Element {
  const { schoolId, isLoading } = useSchoolContext();

  return (
    <EssaysProvider
      search={search}
      navigate={navigate}
      isStudent={false}
      schoolId={schoolId !== null ? Number(schoolId) : null}
      isSchoolLoading={isLoading}>
      {children}
    </EssaysProvider>
  );
}
